import {
  DB_KEY_PREFIXES,
  DB_SYNC_STATE_KEYS,
} from '@omni/config/pouch-db.config';
import { LINE_SUMMARY, ORDER_LIST_ATTRIBUTES } from './order-list';
import {
  MY_ORDERS,
  OrderCaptureDataService,
  withCurrencyCode,
} from '@omni/data-services/order-capture/order-capture.data.service';
import { Observable, Subject } from 'rxjs';
import { AuthenticationService } from '@omni/services/authentication.service';
import { DeviceService } from '@omni/services/device/device.service';
import { DiskService } from '@omni/services/disk/disk.service';
import { DynamicsClientService } from '@omni/data-services/dynamics-client/dynamics-client.service';
import { Injectable } from '@angular/core';
import { SalesOrderState } from '@omni/enums/order-capture/sales-order.enum';
import { idOf } from '@omni/utility/dynamics-row.util';
import { summarizeOrderLines } from './order-line-summary';

export type MyOrderRaw = Record<string, unknown>;

export interface MyOrdersCache {
  rows: MyOrderRaw[];
  /** When the last sync read them; null before the first. */
  syncedAt: string | null;
}

/** From when: the rep's orders created inside the window. */
export interface MyOrdersScope {
  /** ISO start of the rep's offline data window (`offlineDataDuration`, as every synced entity). */
  createdFrom: string;
}

/** A change the server stamped up to this long before the last sync started is read again. */
const CLOCK_SKEW_MS = 5 * 60 * 1000;
/** What the cached rows hold; a cache of another shape is read whole again rather than topped up. */
const CACHE_VERSION = 2;

const EMPTY: MyOrdersCache = { rows: [], syncedAt: null };

/**
 * The orders the rep created (Cancelled left out) within the offline data window, kept on the
 * device by the master sync (full on the first, changes since on the next) so the Order Management
 * tool lists them offline too. Gated on the New Order Flow feature action.
 */
@Injectable({ providedIn: 'root' })
export class MyOrdersService {
  private readonly changedSubject = new Subject<void>();
  /** Deleted by the rep, with when: a sync already under way read them before, so may bring them back. */
  private readonly forgotten = new Map<string, number>();
  /** One sync at a time: the master sync and a Refresh may ask together. */
  private running: Promise<boolean> | null = null;
  /** Cache writes in order, so a delete never lands under a sync's older rows. */
  private writes: Promise<unknown> = Promise.resolve();
  /** The cache as last read or written, for whose it is: a list's reload needn't decrypt it again. */
  private memory: { userId: string; cache: MyOrdersCache } | null = null;

  constructor(
    private readonly orders: OrderCaptureDataService,
    private readonly dynamics: DynamicsClientService,
    private readonly disk: DiskService,
    private readonly device: DeviceService,
    private readonly auth: AuthenticationService,
  ) {}

  /** The cached orders were replaced by a sync. */
  public get changed$(): Observable<void> {
    return this.changedSubject.asObservable();
  }

  /** False when the server could not be read or the device not written; the cache stays as it was. */
  public async sync(
    forceFullSync = false,
    loadFromDbOnly = false,
  ): Promise<boolean> {
    this.running ??= this.run(forceFullSync, loadFromDbOnly).finally(
      () => (this.running = null),
    );
    return this.running;
  }

  public async cached(): Promise<MyOrdersCache> {
    const userId = this.userId();
    if (this.memory?.userId === userId) return this.memory.cache;
    const doc = await this.disk
      .retrieve(DB_KEY_PREFIXES.MY_ORDERS, true)
      .catch(() => null);
    const cache: MyOrdersCache = {
      rows: Array.isArray(doc?.rows) ? doc.rows : [],
      syncedAt: typeof doc?.syncedAt === 'string' ? doc.syncedAt : null,
    };
    this.memory = { userId, cache };
    return cache;
  }

  /** An order the rep deleted: out of the list now rather than at the next sync. */
  public async forget(orderId: string): Promise<void> {
    const id = idOf(orderId);
    this.forgotten.set(id, Date.now());
    const removed = await this.write((cache) => {
      const rows = cache.rows.filter((row) => idOf(row.salesorderid) !== id);
      return rows.length === cache.rows.length ? null : { ...cache, rows };
    });
    if (removed) this.changedSubject.next();
  }

  /** The rep's offline data window in days. */
  public windowDays(): number {
    return this.auth.getOfflineDataDurationInDays();
  }

  /** When the window starts (ISO): an order created before it is not the list's. */
  public windowStart(): string {
    return new Date(
      this.auth.getPastOfflineDataLimitDateInUTCMillisecond(),
    ).toISOString();
  }

  private async run(
    forceFullSync: boolean,
    loadFromDbOnly: boolean,
  ): Promise<boolean> {
    if (!this.orders.isEnabled) return this.reset();
    if (loadFromDbOnly || this.device.isOffline) return true;
    const state = (await this.disk.getSyncState(
      DB_SYNC_STATE_KEYS.SYNC_MY_ORDERS,
    )) ?? { _id: DB_SYNC_STATE_KEYS.SYNC_MY_ORDERS, lastUpdatedTime: null };
    const startedAt = Date.now();
    const scope = { createdFrom: this.windowStart() };
    const since =
      forceFullSync || state.version !== CACHE_VERSION
        ? null
        : (state.lastUpdatedTime as number | null);
    try {
      const read = since
        ? await this.changedSince(since, scope)
        : await this.fetch(myOrdersFetch(null, scope));
      await summarizeOrderLines(this.dynamics, read);
      const syncedAt = new Date(startedAt).toISOString();
      // Filtered as it is written: a delete may have landed while the sync was reading.
      const stored = await this.write(() => ({
        rows: read.filter((row) => !this.forgotten.has(idOf(row.salesorderid))),
        syncedAt,
      }));
      if (!stored) return false;
      // A delete before this sync began is one the server had applied when it was read.
      for (const [id, at] of this.forgotten)
        if (at < startedAt) this.forgotten.delete(id);
      await this.disk.updateSyncState(
        { ...state, lastUpdatedTime: startedAt, version: CACHE_VERSION },
        true,
      );
      this.changedSubject.next();
      return true;
    } catch (error) {
      console.warn('MyOrdersService: syncing the rep’s orders failed', error);
      return false;
    }
  }

  /** The feature is off: a list kept from when it was on goes, and is read whole when it is back. */
  private async reset(): Promise<boolean> {
    const kept = await this.disk.getSyncState(
      DB_SYNC_STATE_KEYS.SYNC_MY_ORDERS,
    );
    if (!kept?.lastUpdatedTime) return true;
    await this.write(() => EMPTY);
    await this.disk.updateSyncState({ ...kept, lastUpdatedTime: null }, true);
    return true;
  }

  /**
   * The cache with what changed since, less what is no longer live: cancelled, deleted, no longer
   * the rep's, or created before the window, which purges it from the device.
   */
  private async changedSince(
    since: number,
    scope: MyOrdersScope,
  ): Promise<MyOrderRaw[]> {
    const from = new Date(since - CLOCK_SKEW_MS).toISOString();
    const [cached, own, lines, deliveries, live] = await Promise.all([
      this.cached(),
      this.fetch(myOrdersFetch(from, scope)),
      this.fetch(myOrdersFetch(from, scope, 'lines')),
      this.fetch(myOrdersFetch(from, scope, 'deliveries')),
      this.fetch(liveIdsFetch(scope)),
    ]);
    return applyMyOrdersDelta(
      cached.rows,
      [...own, ...lines, ...deliveries],
      new Set([...lines, ...deliveries].map((row) => idOf(row.salesorderid))),
      new Set(live.map((row) => idOf(row.salesorderid))),
    );
  }

  /** As the device keeps them, so the copy in memory reads as the stored one: dates as ISO text. */
  private async fetch(fetchXml: string): Promise<MyOrderRaw[]> {
    const rows: MyOrderRaw[] =
      (await this.dynamics.executeFetchQuery('salesorders', fetchXml)) ?? [];
    return rows.map((row) =>
      Object.fromEntries(
        Object.entries(row).map(([key, value]) => [
          key,
          value instanceof Date ? value.toISOString() : value,
        ]),
      ),
    );
  }

  /**
   * Writes the cache in turn with every other write, from the one before; `next` returns null to
   * leave it as it is. False when the device could not keep it.
   */
  private async write(
    next: (cache: MyOrdersCache) => MyOrdersCache | null,
  ): Promise<boolean> {
    const done = this.writes.then(async () => {
      const cache = next(await this.cached());
      if (!cache) return false;
      const stored = await this.disk.updateOrInsert(
        DB_KEY_PREFIXES.MY_ORDERS,
        () => cache,
      );
      if (!stored) return false;
      this.memory = { userId: this.userId(), cache };
      return true;
    });
    this.writes = done.catch(() => false);
    return done;
  }

  private userId(): string {
    return idOf(this.auth.user?.systemUserID);
  }
}

/** Procedure logs are orders too, by type; they stay procedure logs. */
const NOT_PROCEDURE_LOGS =
  '<filter type="or"><condition attribute="indskr_ordertype" operator="null" />' +
  '<condition attribute="indskr_ordertype" operator="not-in"><value>548910001</value><value>548910002</value></condition></filter>';

/**
 * The rep's own orders but Cancelled, created within the window: SaveOrder creates them as SYSTEM,
 * so on their behalf. An order cancelled since the last sync drops out with the live ids.
 */
function scoped(scope: MyOrdersScope): string {
  return (
    `<condition attribute="statecode" operator="ne" value="${SalesOrderState.Canceled}" />` +
    `<condition attribute="createdon" operator="on-or-after" value="${scope.createdFrom}" />` +
    `${MY_ORDERS}${NOT_PROCEDURE_LOGS}`
  );
}

/**
 * An order's lines and deliveries are rows of their own: one changed (a quantity, a product, a
 * date) need not touch the order's `modifiedon`, so a delta also asks by theirs.
 */
const CHILDREN = {
  lines: { entity: 'salesorderdetail', link: 'salesorderid' },
  deliveries: { entity: 'indskr_orderdelivery', link: 'indskr_salesorder' },
} as const;

/**
 * The rep's orders; with `modifiedSince`, every one changed since, or with `child` every one whose
 * lines or deliveries changed since.
 */
export function myOrdersFetch(
  modifiedSince: string | null,
  scope: MyOrdersScope,
  child?: keyof typeof CHILDREN,
): string {
  const attributes = ORDER_LIST_ATTRIBUTES.map(
    (name) => `<attribute name="${name}" />`,
  ).join('');
  const since = `<condition attribute="modifiedon" operator="ge" value="${modifiedSince}" />`;
  const changed = modifiedSince && !child ? since : '';
  const changedChild =
    modifiedSince && child
      ? `<link-entity name="${CHILDREN[child].entity}" from="${CHILDREN[child].link}" to="salesorderid" link-type="inner">` +
        `<filter type="and">${since}</filter></link-entity>`
      : '';
  return withCurrencyCode(
    // Distinct: an order joins once per changed line or delivery. It pages reliably only on a
    // unique column; the list sorts the rows itself.
    `<fetch distinct="true"><entity name="salesorder">${attributes}${changedChild}` +
      `<filter type="and">${changed}${scoped(scope)}</filter>` +
      `<order attribute="salesorderid" /></entity></fetch>`,
  );
}

export function liveIdsFetch(scope: MyOrdersScope): string {
  return (
    `<fetch><entity name="salesorder"><attribute name="salesorderid" />` +
    `<filter type="and">${scoped(scope)}</filter><order attribute="salesorderid" /></entity></fetch>`
  );
}

/**
 * Changed rows replace their cached copy, keeping its line summary unless `linesChanged` names the
 * order; any row no longer live goes.
 */
export function applyMyOrdersDelta(
  cached: readonly MyOrderRaw[],
  changed: readonly MyOrderRaw[],
  linesChanged: ReadonlySet<string>,
  live: ReadonlySet<string>,
): MyOrderRaw[] {
  const byId = new Map(cached.map((row) => [idOf(row.salesorderid), row]));
  for (const row of changed) {
    const id = idOf(row.salesorderid);
    const before = byId.get(id);
    byId.set(
      id,
      before && !linesChanged.has(id) ? { ...row, ...summaryOf(before) } : row,
    );
  }
  return [...byId.values()].filter((row) => live.has(idOf(row.salesorderid)));
}

function summaryOf(row: MyOrderRaw): MyOrderRaw {
  return Object.fromEntries(
    Object.values(LINE_SUMMARY)
      .filter((key) => key in row)
      .map((key) => [key, row[key]]),
  );
}
